import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Loader2, X } from 'lucide-react';
import type {
  BackofficeLink,
  BackofficeLinkQrCodeSettings,
  BackofficeLinkTargetInput,
  BackofficeLinkTargetType,
  BackofficeStudyOption,
} from '../types';
import { LinkQrCodePanel } from './LinkQrCodePanel';

const INTERNAL_URL_BASE = 'https://navainsights.io/';
const LINK_PUBLIC_BASE = 'https://l.navainsights.io/';

type LinkEditorModalProps = {
  mode: 'create' | 'edit' | 'restore';
  link?: BackofficeLink | null;
  submitting: boolean;
  error: string | null;
  onClose: () => void;
  onArchive?: (() => Promise<void> | void) | undefined;
  onConfirmArchive?: (() => boolean) | undefined;
  onCheckSlugAvailability: (slug: string) => Promise<boolean>;
  onGenerateUniqueSlug: () => Promise<string>;
  onSearchStudies: (query: string) => Promise<BackofficeStudyOption[]>;
  onSubmit: (input: {
    slug?: string;
    target?: BackofficeLinkTargetInput;
    description?: string;
    qrCode?: BackofficeLinkQrCodeSettings;
  }) => Promise<void> | void;
};

function getInitialTargetType(link: BackofficeLink | null | undefined): BackofficeLinkTargetType {
  return link?.currentDestination?.targetType ?? 'external_url';
}

function getInitialUrlValue(link: BackofficeLink | null | undefined): string {
  return link?.currentDestination?.targetType === 'external_url'
    ? (link.currentDestination.url ?? '')
    : '';
}

function getInitialInternalPath(link: BackofficeLink | null | undefined): string {
  if (link?.currentDestination?.targetType !== 'internal_url') {
    return '';
  }

  const currentUrl = link.currentDestination.url ?? '';
  if (!currentUrl.startsWith(INTERNAL_URL_BASE)) {
    return currentUrl.replace(/^https?:\/\/[^/]+\//, '');
  }

  return currentUrl.slice(INTERNAL_URL_BASE.length);
}

function buildInternalUrl(relativePath: string): string {
  const normalizedPath = relativePath.trim().replace(/^\/+/, '');
  return `${INTERNAL_URL_BASE}${normalizedPath}`;
}

function slugifyDescription(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

export function LinkEditorModal({
  mode,
  link,
  submitting,
  error,
  onClose,
  onArchive,
  onConfirmArchive,
  onCheckSlugAvailability,
  onGenerateUniqueSlug,
  onSearchStudies,
  onSubmit,
}: LinkEditorModalProps) {
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const [slugValue, setSlugValue] = useState(link?.slug ?? '');
  const [slugError, setSlugError] = useState<string | null>(null);
  const [generatingSlug, setGeneratingSlug] = useState(false);
  const [targetType, setTargetType] = useState<BackofficeLinkTargetType>(
    getInitialTargetType(link)
  );
  const [urlValue, setUrlValue] = useState(getInitialUrlValue(link));
  const [internalPath, setInternalPath] = useState(getInitialInternalPath(link));
  const [studyQuery, setStudyQuery] = useState(link?.currentDestination?.project?.title ?? '');
  const [studyOptions, setStudyOptions] = useState<BackofficeStudyOption[]>([]);
  const [searchingStudies, setSearchingStudies] = useState(false);
  const [restoreMode, setRestoreMode] = useState<'keep' | 'change'>('keep');
  const [descriptionValue, setDescriptionValue] = useState(
    link?.currentDestination?.description ?? ''
  );
  const [foregroundColor, setForegroundColor] = useState(link?.qrCode.foregroundColor ?? '#000000');
  const [showLogo, setShowLogo] = useState(link?.qrCode.showLogo ?? true);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  useEffect(() => {
    setSlugValue(link?.slug ?? '');
    setSlugError(null);
    setTargetType(getInitialTargetType(link));
    setUrlValue(getInitialUrlValue(link));
    setInternalPath(getInitialInternalPath(link));
    setStudyQuery(link?.currentDestination?.project?.title ?? '');
    setStudyOptions([]);
    setRestoreMode('keep');
    setDescriptionValue(link?.currentDestination?.description ?? '');
    setForegroundColor(link?.qrCode.foregroundColor ?? '#000000');
    setShowLogo(link?.qrCode.showLogo ?? true);
  }, [link, mode]);

  useEffect(() => {
    if (mode !== 'create') {
      return;
    }

    let active = true;
    setGeneratingSlug(true);
    setSlugError(null);
    onGenerateUniqueSlug()
      .then((slug) => {
        if (active) {
          setSlugValue(slug);
        }
      })
      .catch(() => {
        if (active) {
          setSlugError('Failed to generate a unique slug.');
        }
      })
      .finally(() => {
        if (active) {
          setGeneratingSlug(false);
        }
      });

    return () => {
      active = false;
    };
  }, [mode, onGenerateUniqueSlug]);

  async function handleRegenerateSlug() {
    setGeneratingSlug(true);
    setSlugError(null);
    try {
      setSlugValue(await onGenerateUniqueSlug());
    } catch {
      setSlugError('Failed to generate a unique slug.');
    } finally {
      setGeneratingSlug(false);
    }
  }

  function handleGenerateSlugFromDescription() {
    const nextSlug = slugifyDescription(descriptionValue);
    if (!nextSlug) {
      setSlugError('Enter a description to generate a slug.');
      return;
    }
    setSlugError(null);
    setSlugValue(nextSlug);
  }

  const shouldShowTargetFields = mode !== 'restore' || restoreMode === 'change';
  const title = mode === 'create' ? 'Create link' : mode === 'edit' ? 'Edit link' : 'Restore link';
  const shouldShowQrCode = mode === 'edit' && link?.status === 'active' && Boolean(link.shortUrl);

  async function handleSearchStudies() {
    if (!studyQuery.trim()) {
      setStudyOptions([]);
      return;
    }
    setSearchingStudies(true);
    try {
      setStudyOptions(await onSearchStudies(studyQuery));
    } finally {
      setSearchingStudies(false);
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const description = descriptionValue.trim() || undefined;
    const slug = String(form.get('slug') ?? '').trim();

    if (mode === 'restore' && String(form.get('reuseMode') ?? 'keep') === 'keep') {
      await onSubmit(description ? { description } : {});
      return;
    }

    if (mode === 'create') {
      const available = await onCheckSlugAvailability(slug);
      if (!available) {
        setSlugError('Slug is already in use. Generate a new one before saving.');
        return;
      }
      setSlugError(null);
    }

    let target: BackofficeLinkTargetInput;
    if (targetType === 'study') {
      target = {
        targetType,
        projectId: String(form.get('projectId') ?? ''),
      };
    } else if (targetType === 'internal_url') {
      target = {
        targetType,
        url: buildInternalUrl(String(form.get('relativePath') ?? '')),
      };
    } else {
      target = {
        targetType,
        url: String(form.get('url') ?? '').trim(),
      };
    }

    await onSubmit({
      ...(mode === 'create' ? { slug } : {}),
      target,
      ...(description ? { description } : {}),
      ...(mode === 'edit' && link ? { qrCode: { foregroundColor, showLogo } } : {}),
    });
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-950/45 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="link-editor-title"
        className="w-full max-w-2xl rounded-xl bg-white shadow-2xl"
      >
        <div className="flex items-start justify-between border-b border-neutral-200 px-6 py-5">
          <div>
            <h2
              id="link-editor-title"
              className="font-inter-tight text-xl font-semibold text-neutral-900"
            >
              {title}
            </h2>
          </div>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="rounded-md p-2 text-neutral-500 hover:bg-backoffice-50 hover:text-neutral-900"
          >
            <X className="size-5" />
          </button>
        </div>
        <form className="space-y-4 px-6 py-5" onSubmit={(event) => void handleSubmit(event)}>
          {error || slugError ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-small font-semibold text-red-700">
              {error ?? slugError}
            </div>
          ) : null}

          {mode === 'create' ? (
            <div className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              <label htmlFor="link-slug-input">Slug</label>
              <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_auto]">
                <div className="flex min-w-0 items-center">
                  <span
                    aria-label="Short link base"
                    className="shrink-0 text-body font-semibold text-neutral-500"
                  >
                    {LINK_PUBLIC_BASE}
                  </span>
                  <input
                    id="link-slug-input"
                    name="slug"
                    required
                    value={slugValue}
                    onChange={(event) => setSlugValue(event.target.value)}
                    className="h-10 min-w-0 flex-1 rounded-lg border border-neutral-200 px-3 text-body text-neutral-900"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void handleRegenerateSlug()}
                  disabled={submitting || generatingSlug}
                  className="rounded-lg border border-neutral-200 px-4 text-small font-semibold text-neutral-700 disabled:opacity-60"
                >
                  {generatingSlug ? 'Generating…' : 'Regenerate'}
                </button>
              </div>
            </div>
          ) : null}

          {mode === 'restore' ? (
            <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
              Restore mode
              <select
                name="reuseMode"
                value={restoreMode}
                onChange={(event) => setRestoreMode(event.target.value as 'keep' | 'change')}
                className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
              >
                <option value="keep">Keep previous target</option>
                <option value="change">Choose a new target</option>
              </select>
            </label>
          ) : null}

          {shouldShowTargetFields ? (
            <>
              <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
                Target type
                <select
                  value={targetType}
                  onChange={(event) =>
                    setTargetType(event.target.value as BackofficeLinkTargetType)
                  }
                  className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
                >
                  <option value="external_url">External URL</option>
                  <option value="internal_url">Internal URL</option>
                  <option value="study">Study</option>
                </select>
              </label>

              {targetType === 'study' ? (
                <div className="space-y-3">
                  <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
                    Search study
                    <div className="flex gap-2">
                      <input
                        value={studyQuery}
                        onChange={(event) => setStudyQuery(event.target.value)}
                        className="h-10 flex-1 rounded-lg border border-neutral-200 px-3 text-body text-neutral-900"
                      />
                      <button
                        type="button"
                        onClick={() => void handleSearchStudies()}
                        className="rounded-lg bg-backoffice-600 px-4 text-small font-semibold text-white"
                      >
                        Search
                      </button>
                    </div>
                  </label>
                  {searchingStudies ? (
                    <div className="text-small text-neutral-500">Searching studies…</div>
                  ) : null}
                  <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
                    Study
                    <select
                      name="projectId"
                      required
                      defaultValue={link?.currentDestination?.projectId ?? ''}
                      className="h-10 rounded-lg border border-neutral-200 bg-white px-3 text-body text-neutral-900"
                    >
                      <option value="">Select a study</option>
                      {studyOptions.map((study) => (
                        <option key={study.id} value={study.id}>
                          {study.title}{' '}
                          {study.resolutionState === 'awaiting_publish' ? '(awaiting publish)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              ) : targetType === 'internal_url' ? (
                <div className="space-y-1">
                  <span className="text-small font-semibold text-neutral-700">URL</span>
                  <div className="flex min-w-0 items-center gap-0">
                    <span
                      aria-label="Internal URL base"
                      className="shrink-0 text-body font-semibold text-neutral-500"
                    >
                      {INTERNAL_URL_BASE}
                    </span>
                    <input
                      name="relativePath"
                      required
                      value={internalPath}
                      onChange={(event) => setInternalPath(event.target.value)}
                      placeholder="branding-guideline"
                      aria-label="Relative path"
                      className="h-10 min-w-0 flex-1 rounded-lg border border-neutral-200 px-3 text-body text-neutral-900"
                    />
                  </div>
                </div>
              ) : (
                <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
                  URL
                  <input
                    name="url"
                    required
                    type="url"
                    value={urlValue}
                    onChange={(event) => setUrlValue(event.target.value)}
                    className="h-10 rounded-lg border border-neutral-200 px-3 text-body text-neutral-900"
                  />
                </label>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-small text-neutral-700">
              Restoring this link will keep its current target unchanged.
            </div>
          )}

          <label className="flex flex-col gap-1 text-small font-semibold text-neutral-700">
            Description
            <textarea
              name="description"
              value={descriptionValue}
              onChange={(event) => setDescriptionValue(event.target.value)}
              rows={3}
              className="rounded-lg border border-neutral-200 px-3 py-2 text-body text-neutral-900"
            />
            {mode === 'create' ? (
              <div className="pt-1">
                <button
                  type="button"
                  onClick={handleGenerateSlugFromDescription}
                  disabled={submitting}
                  className="rounded-lg border border-neutral-200 px-4 py-2 text-small font-semibold text-neutral-700 disabled:opacity-60"
                >
                  Generate slug from description
                </button>
              </div>
            ) : null}
          </label>

          {shouldShowQrCode ? (
            <div className="space-y-4">
              <LinkQrCodePanel
                slug={link.slug}
                shortUrl={link.shortUrl}
                qrCode={{ foregroundColor, showLogo }}
                showDownloadButton={false}
                detailsContent={
                  <div className="space-y-3">
                    <label className="flex flex-col gap-2 text-small font-semibold text-neutral-700">
                      QR code color
                      <div className="flex items-center gap-3">
                        <input
                          type="color"
                          value={foregroundColor}
                          onChange={(event) => setForegroundColor(event.target.value)}
                          aria-label="QR code color"
                          className="h-10 w-14 rounded-lg border border-neutral-200 bg-white p-1"
                        />
                        <input
                          type="text"
                          value={foregroundColor}
                          onChange={(event) => setForegroundColor(event.target.value)}
                          className="h-10 flex-1 rounded-lg border border-neutral-200 px-3 text-body text-neutral-900"
                        />
                      </div>
                    </label>
                    <label className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3 text-body text-neutral-900">
                      <input
                        type="checkbox"
                        checked={showLogo}
                        onChange={(event) => setShowLogo(event.target.checked)}
                        className="h-4 w-4 rounded border-neutral-300"
                      />
                      <span>Show Nava icon</span>
                    </label>
                  </div>
                }
              />
            </div>
          ) : null}

          <div className="flex justify-end gap-3">
            {mode === 'edit' && onArchive ? (
              <button
                type="button"
                onClick={() => {
                  if (!onConfirmArchive || onConfirmArchive()) {
                    void onArchive();
                  }
                }}
                disabled={submitting}
                className="rounded-lg border border-neutral-200 px-4 py-2 text-small font-semibold text-neutral-700 disabled:opacity-60"
              >
                Archive
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-neutral-200 px-4 py-2 text-small font-semibold text-neutral-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center rounded-lg bg-backoffice-600 px-4 py-2 text-small font-semibold text-white disabled:opacity-60"
            >
              {submitting ? (
                <span className="inline-flex items-center whitespace-nowrap">
                  <Loader2 className="mr-1 size-4 animate-spin" />
                  Save
                </span>
              ) : mode === 'restore' ? (
                'Restore link'
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
